import PageTitle from '@/components/Admin/PageTitle';
import Table from '@/components/Admin/Table';
import React, { useState, useEffect, useMemo } from 'react';
import { styled } from 'styled-components';
import { DropdownActionType } from '@/types/shared';
import { useQuery } from '@tanstack/react-query';
import { fetchTransactions } from '@/api/transaction';
import { useUIStore } from '@/store/useUIStore';
import { colors } from '@/constants/colors';
import { TableTransaction } from './Home';
import DateRangePicker from '@/components/Admin/UI/DateRangePicker';
import {
   AreaChart,
   Area,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   ResponsiveContainer,
} from 'recharts';
import { currencyFormat } from '@/utils/currencyFormatter';

const STORAGE_KEY = 'smartfi-transaction-filters';
const PER_PAGE = 15;

type TransactionFilters = {
   dateFrom: string;
   dateTo: string;
};

const loadFilters = (): TransactionFilters => {
   try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
   } catch {}
   return { dateFrom: '', dateTo: '' };
};

const Transactions = () => {
   const { refresh, handleShow } = useUIStore();
   const [filters, setFilters] = useState<TransactionFilters>(loadFilters);
   const [page, setPage] = useState(1);

   const {
      data: response,
      isLoading,
      error,
   } = useQuery({
      queryKey: ['transaction', refresh],
      queryFn: fetchTransactions,
   });

   // Persist filters
   useEffect(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
   }, [filters]);

   // Reset page when filters change
   useEffect(() => {
      setPage(1);
   }, [filters]);

   const updateFilter = (key: keyof TransactionFilters, value: string) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
   };

   const transactionHeadData = [
      { type: 'title', value: 'Date' },
      { type: 'title', value: 'Status' },
      { type: 'title', value: 'Voucher Code' },
      { type: 'title', value: 'Store' },
      { type: 'title', value: 'Net Amount' },
      { type: 'title', value: 'Contact' },
      { type: 'title', value: 'SMS' },
      { type: 'title', value: 'Action' },
   ];

   const transactionData: TableTransaction[] =
      response?.transactions.map((transaction: TableTransaction) => ({
         _id: transaction._id,
         updatedAt: transaction.updatedAt,
         createdAt: transaction.createdAt,
         status: transaction.status,
         voucherCode: transaction.voucherCode,
         store: transaction.store,
         amount: transaction.amount,
         platformFee: transaction.platformFee,
         xenditFee: transaction.xenditFee,
         netAmount: transaction.netAmount,
         vat: transaction.vat,
         customer: transaction.customer,
         smsStatus: transaction.smsStatus,
         smsError: transaction.smsError,
      })) || [];

   // Apply date filter
   const filteredData = useMemo(() => {
      return transactionData.filter((t) => {
         const txDate = new Date(t.updatedAt);
         if (filters.dateFrom) {
            const from = new Date(filters.dateFrom);
            from.setHours(0, 0, 0, 0);
            if (txDate < from) return false;
         }
         if (filters.dateTo) {
            const to = new Date(filters.dateTo);
            to.setHours(23, 59, 59, 999);
            if (txDate > to) return false;
         }
         return true;
      });
   }, [transactionData, filters]);

   // Pagination
   const totalPages = Math.ceil(filteredData.length / PER_PAGE);
   const paginatedData = filteredData.slice(
      (page - 1) * PER_PAGE,
      page * PER_PAGE
   );

   const hasActiveFilters = filters.dateFrom !== '' || filters.dateTo !== '';

   // Chart data — aggregate net amount by date
   const chartData = useMemo(() => {
      const successOnly = filteredData.filter((t) => t.status === 'success');
      const grouped: Record<string, number> = {};

      successOnly.forEach((t) => {
         const d = new Date(t.updatedAt);
         const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
         grouped[key] = (grouped[key] || 0) + t.netAmount;
      });

      return Object.entries(grouped)
         .sort(([a], [b]) => a.localeCompare(b))
         .map(([date, total]) => {
            const d = new Date(date + 'T00:00:00');
            return {
               date,
               label: d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric' }),
               total: Math.round(total * 100) / 100,
            };
         });
   }, [filteredData]);

   const totalNetAmount = useMemo(() => {
      return filteredData
         .filter((t) => t.status === 'success')
         .reduce((sum, t) => sum + t.netAmount, 0);
   }, [filteredData]);

   const dropdownActions: DropdownActionType<TableTransaction>[] = [
      {
         label: 'View Details',
         onClick: (data: TableTransaction) => {
            handleShow({
               template: 'transaction-detail',
               title: `Transaction Details`,
               windowType: 'desktop',
               data: data,
            });
         },
         icon: 'file-copy-line',
      },
   ];

   return (
      <Wrapper>
         <PageTitle title="Transactions" actions={[]} />

         <FilterBar>
            <DateRangePicker
               dateFrom={filters.dateFrom}
               dateTo={filters.dateTo}
               onChange={(from, to) =>
                  setFilters({ dateFrom: from, dateTo: to })
               }
            />

            <ResultCount>
               {filteredData.length} transaction{filteredData.length !== 1 ? 's' : ''}
            </ResultCount>
         </FilterBar>

         {!isLoading && !error && chartData.length > 0 && (
            <ChartSection>
               <ChartHeader>
                  <ChartTitle>Revenue Overview</ChartTitle>
                  <ChartTotal>
                     {currencyFormat(totalNetAmount, false)}
                  </ChartTotal>
               </ChartHeader>
               <ResponsiveContainer width="100%" height={220}>
                  <AreaChart
                     data={chartData}
                     margin={{ top: 5, right: 20, left: 0, bottom: 0 }}
                  >
                     <defs>
                        <linearGradient id="netAmountGradient" x1="0" y1="0" x2="0" y2="1">
                           <stop offset="0%" stopColor={colors.PRIMARY} stopOpacity={0.25} />
                           <stop offset="95%" stopColor={colors.PRIMARY} stopOpacity={0} />
                        </linearGradient>
                     </defs>
                     <CartesianGrid strokeDasharray="3 3" stroke={colors.GRAY13} vertical={false} />
                     <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: colors.GRAY5 }}
                        axisLine={false}
                        tickLine={false}
                     />
                     <YAxis
                        tick={{ fontSize: 11, fill: colors.GRAY5 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => `₱${v}`}
                        width={60}
                     />
                     <Tooltip
                        contentStyle={{
                           borderRadius: '8px',
                           border: `1px solid ${colors.GRAY13}`,
                           boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                           fontSize: '13px',
                        }}
                        formatter={(value: any) => [currencyFormat(Number(value), false), 'Net Amount']}
                        labelStyle={{ fontWeight: 600, marginBottom: 4 }}
                     />
                     <Area
                        type="monotone"
                        dataKey="total"
                        stroke={colors.PRIMARY}
                        strokeWidth={2}
                        fill="url(#netAmountGradient)"
                        dot={chartData.length <= 31 ? { r: 3, fill: colors.PRIMARY, strokeWidth: 0 } : false}
                        activeDot={{ r: 5, fill: colors.PRIMARY, stroke: 'white', strokeWidth: 2 }}
                     />
                  </AreaChart>
               </ResponsiveContainer>
            </ChartSection>
         )}

         {isLoading ? (
            <LoadingText>Loading transactions...</LoadingText>
         ) : error ? (
            <ErrorText>Error loading transactions: {error.message}</ErrorText>
         ) : (
            <>
               <Table<TableTransaction>
                  headData={transactionHeadData}
                  data={paginatedData}
                  grid={`110px 1fr 2fr 1fr 1fr 1fr 90px auto`}
                  type="transaction"
                  dropdownData={dropdownActions}
                  onRowClick={(data) => {
                     handleShow({
                        template: 'transaction-detail',
                        title: 'Transaction Details',
                        windowType: 'desktop',
                        data: data,
                     });
                  }}
               />

               {totalPages > 1 && (
                  <Pagination>
                     <PageBtn
                        disabled={page === 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                     >
                        Prev
                     </PageBtn>

                     <PageNumbers>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                           .filter((p) => {
                              if (totalPages <= 7) return true;
                              if (p === 1 || p === totalPages) return true;
                              if (Math.abs(p - page) <= 1) return true;
                              return false;
                           })
                           .reduce<(number | string)[]>((acc, p, idx, arr) => {
                              if (idx > 0 && p - (arr[idx - 1] as number) > 1) {
                                 acc.push('...');
                              }
                              acc.push(p);
                              return acc;
                           }, [])
                           .map((p, idx) =>
                              typeof p === 'string' ? (
                                 <Ellipsis key={`e-${idx}`}>...</Ellipsis>
                              ) : (
                                 <PageNum
                                    key={p}
                                    $active={p === page}
                                    onClick={() => setPage(p)}
                                 >
                                    {p}
                                 </PageNum>
                              )
                           )}
                     </PageNumbers>

                     <PageBtn
                        disabled={page === totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                     >
                        Next
                     </PageBtn>
                  </Pagination>
               )}
            </>
         )}
      </Wrapper>
   );
};

const Wrapper = styled.div``;

const FilterBar = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
   padding: 12px 1rem;
   flex-wrap: wrap;
   border-bottom: 1px solid ${colors.GRAY13};
   @media (max-width: 768px) {
      padding: 10px 12px;
      gap: 8px;
   }
`;

const ResultCount = styled.span`
   font-size: 13px;
   color: ${colors.GRAY5};
   margin-left: auto;
   align-self: flex-end;
   padding-bottom: 6px;
`;

const Pagination = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 8px;
   padding: 16px 1rem;
`;

const PageBtn = styled.button<{ disabled?: boolean }>`
   display: flex;
   align-items: center;
   gap: 4px;
   background: none;
   border: 1px solid ${({ disabled }) => (disabled ? colors.GRAY12 : colors.GRAY10)};
   border-radius: 4px;
   padding: 6px 12px;
   font-size: 13px;
   color: ${({ disabled }) => (disabled ? colors.GRAY10 : colors.GRAY1)};
   cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

   &:hover:not(:disabled) {
      background: ${colors.GRAY14};
   }
`;

const PageNumbers = styled.div`
   display: flex;
   align-items: center;
   gap: 4px;
`;

const PageNum = styled.button<{ $active: boolean }>`
   background: ${({ $active }) => ($active ? colors.PRIMARY : 'none')};
   color: ${({ $active }) => ($active ? 'white' : colors.GRAY1)};
   border: 1px solid ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 4px;
   width: 32px;
   height: 32px;
   font-size: 13px;
   cursor: pointer;

   &:hover {
      background: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY14)};
   }
`;

const Ellipsis = styled.span`
   color: ${colors.GRAY5};
   font-size: 13px;
   padding: 0 4px;
`;

const ChartSection = styled.div`
   padding: 20px 1rem;
   border-bottom: 1px solid ${colors.GRAY13};
   @media (max-width: 768px) {
      padding: 14px 12px;
   }
`;

const ChartHeader = styled.div`
   display: flex;
   align-items: baseline;
   justify-content: space-between;
   margin-bottom: 16px;
   @media (max-width: 768px) {
      margin-bottom: 10px;
   }
`;

const ChartTitle = styled.h3`
   font-size: 14px;
   font-weight: 600;
   color: ${colors.GRAY1};
`;

const ChartTotal = styled.span`
   font-size: 22px;
   font-weight: 700;
   color: ${colors.PRIMARY};
   @media (max-width: 768px) {
      font-size: 18px;
   }
`;

const LoadingText = styled.div`
   text-align: center;
   padding: 2rem;
   color: ${colors.GRAY5};
`;

const ErrorText = styled.div`
   text-align: center;
   padding: 2rem;
   color: ${colors.DANGER};
`;

export default Transactions;
